/**
 * Consumable Crafting Recipes
 * Potions, buffs, and usable items
 *
 * These recipes produce stackable items that can be used in combat/dungeons
 *
 * NOTE: All costs are 5x base for meaningful investment
 */

export const CONSUMABLE_RECIPES = {
  // ===========================================
  // TIER 1 - Basic Consumables (70% quality / 30s craft)
  // ===========================================
  craft_minor_health_potion: {
    id: 'craft_minor_health_potion',
    name: 'Minor Health Potion',
    icon: '🧪',
    category: 'consumables',
    tier: 1,
    description: 'Restores 50 HP. A basic healing brew.',
    cost: {
      resources: { food: 75, gold: 50 },
      materials: {},
    },
    output: {
      type: 'consumable',
      itemId: 'minorHealthPotion',
      quantity: 3,
    },
  },

  craft_bread: {
    id: 'craft_bread',
    name: 'Fresh Bread',
    icon: '🍞',
    category: 'consumables',
    tier: 1,
    description: 'Simple but nourishing. Restores 25 HP.',
    cost: {
      resources: { food: 50, gold: 25 },
      materials: {},
    },
    output: {
      type: 'consumable',
      itemId: 'bread',
      quantity: 5,
    },
  },

  // ===========================================
  // TIER 2 - Standard Consumables (80% quality / 60s craft)
  // ===========================================
  craft_health_potion: {
    id: 'craft_health_potion',
    name: 'Health Potion',
    icon: '🧪',
    category: 'consumables',
    tier: 2,
    description: 'Restores 150 HP. Standard adventurer fare.',
    cost: {
      resources: { food: 150, gold: 125 },
      materials: { essence: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'healthPotion',
      quantity: 3,
    },
    requiresTech: 'medicine',
  },

  craft_strength_potion: {
    id: 'craft_strength_potion',
    name: 'Potion of Strength',
    icon: '💪',
    category: 'consumables',
    tier: 2,
    description: '+20% Attack for 5 minutes.',
    cost: {
      resources: { gold: 200 },
      materials: { essence: 15, orcTusk: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'strengthPotion',
      quantity: 2,
    },
    requiresTech: 'medicine',
  },

  craft_defense_potion: {
    id: 'craft_defense_potion',
    name: 'Potion of Defense',
    icon: '🛡️',
    category: 'consumables',
    tier: 2,
    description: '+20% Defense for 5 minutes.',
    cost: {
      resources: { gold: 200 },
      materials: { essence: 15, thickHide: 5 },
    },
    output: {
      type: 'consumable',
      itemId: 'defensePotion',
      quantity: 2,
    },
    requiresTech: 'medicine',
  },

  craft_swiftness_potion: {
    id: 'craft_swiftness_potion',
    name: 'Potion of Swiftness',
    icon: '💨',
    category: 'consumables',
    tier: 2,
    description: '+2 Speed for 5 minutes.',
    cost: {
      resources: { gold: 175 },
      materials: { essence: 10, wolfFang: 15 },
    },
    output: {
      type: 'consumable',
      itemId: 'swiftnessPotion',
      quantity: 2,
    },
    requiresTech: 'medicine',
  },

  craft_cooked_meat: {
    id: 'craft_cooked_meat',
    name: 'Cooked Meat',
    icon: '🍖',
    category: 'consumables',
    tier: 2,
    description: 'Hearty meal. Restores 75 HP.',
    cost: {
      resources: { food: 125, gold: 50 },
      materials: { leather: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'cookedMeat',
      quantity: 3,
    },
  },

  // ===========================================
  // TIER 3 - Advanced Consumables (90% quality / 2m craft)
  // ===========================================
  craft_greater_health_potion: {
    id: 'craft_greater_health_potion',
    name: 'Greater Health Potion',
    icon: '🧪',
    category: 'consumables',
    tier: 3,
    description: 'Restores 400 HP. Powerful healing.',
    cost: {
      resources: { food: 300, gold: 400 },
      materials: { essence: 25, crystal: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'greaterHealthPotion',
      quantity: 2,
    },
    requiresTech: 'alchemy',
  },

  craft_scroll_fortitude: {
    id: 'craft_scroll_fortitude',
    name: 'Scroll of Fortitude',
    icon: '📜',
    category: 'consumables',
    tier: 3,
    description: '+100 Max HP for 10 minutes.',
    cost: {
      resources: { gold: 500, knowledge: 100 },
      materials: { essence: 20, rune: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'scrollOfFortitude',
      quantity: 2,
    },
    requiresTech: 'alchemy',
  },

  craft_scroll_precision: {
    id: 'craft_scroll_precision',
    name: 'Scroll of Precision',
    icon: '📜',
    category: 'consumables',
    tier: 3,
    description: '+15% Critical Chance for 10 minutes.',
    cost: {
      resources: { gold: 500, knowledge: 100 },
      materials: { essence: 20, crystal: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'scrollOfPrecision',
      quantity: 2,
    },
    requiresTech: 'alchemy',
  },

  // ===========================================
  // TIER 4 - Elite Consumables (100% quality / 4m craft)
  // ===========================================
  craft_superior_health_potion: {
    id: 'craft_superior_health_potion',
    name: 'Superior Health Potion',
    icon: '🧪',
    category: 'consumables',
    tier: 4,
    description: 'Restores 1000 HP. The best healing available.',
    cost: {
      resources: { food: 500, gold: 1000, magic: 100 },
      materials: { essence: 50, crystal: 25, soulShard: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'superiorHealthPotion',
      quantity: 2,
    },
    requiresTech: 'medicine_advanced',
  },

  craft_elixir_power: {
    id: 'craft_elixir_power',
    name: 'Elixir of Power',
    icon: '⚡',
    category: 'consumables',
    tier: 4,
    description: '+30% Attack and Defense for 10 minutes.',
    cost: {
      resources: { gold: 1250, magic: 150 },
      materials: { soulShard: 15, essence: 40, demonHorn: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'elixirOfPower',
      quantity: 1,
    },
    requiresTech: 'medicine_advanced',
  },

  craft_blessing_token: {
    id: 'craft_blessing_token',
    name: 'Blessing Token',
    icon: '🙏',
    category: 'consumables',
    tier: 4,
    description: '+50% XP for 30 minutes.',
    cost: {
      resources: { gold: 1500, knowledge: 250, culture: 100 },
      materials: { divineEssence: 5, crystal: 15 },
    },
    output: {
      type: 'consumable',
      itemId: 'blessingToken',
      quantity: 1,
    },
    requiresTech: 'theology',
  },

  craft_lucky_charm: {
    id: 'craft_lucky_charm',
    name: 'Lucky Charm',
    icon: '🍀',
    category: 'consumables',
    tier: 4,
    description: '+20% Loot Quality for 15 minutes.',
    cost: {
      resources: { gold: 1000, gems: 25 },
      materials: { essence: 25, crystal: 15, rune: 10 },
    },
    output: {
      type: 'consumable',
      itemId: 'luckyCharm',
      quantity: 1,
    },
    requiresTech: 'enchanting',
  },
};

export default CONSUMABLE_RECIPES;
